/**
 * Routing module — hash-based navigation con react-router-dom.
 *
 * La URL usa hash (#) para que nunca llegue al servidor (compatible con
 * Cloudflare Access). React Router maneja el renderizado vía <Routes> y
 * este módulo provee los hooks para sincronizar átomos ↔ URL.
 *
 * URLs (parámetros dinámicos al final):
 *   #/meetings
 *   #/new-meeting
 *   #/new-meeting/:processingId
 *   #/identify/:processingId
 *   #/review/:processingId
 *   #/export/:processingId
 *   #/viewer/:processingId/:srtFile?
 *   #/corrections/:processingId
 *   #/structure/:processingId/:tab?
 *   #/report/:processingId
 *   #/review-report/:processingId
 *   #/admin/:tab?
 */

import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import {
  adminTabAtom,
  currentProcessingIdAtom,
  structureTabAtom,
  viewerSrtFileAtom,
  oracleJsonAtom,
} from "../state/atoms.ts";
import type { AdminTab, StructureTab, ViewerSrtFile } from "../state/atoms.ts";

// ---------------------------------------------------------------------------
// useNavigateTo — hook de navegación tipada que reemplaza setScreen().
// ---------------------------------------------------------------------------

export function useNavigateTo() {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      meetings: () => navigate("/meetings"),
      newMeeting: () => navigate("/new-meeting"),
      resumeMeeting: (id: string) => navigate(`/new-meeting/${id}`),
      admin: (tab?: AdminTab) => navigate(`/admin/${tab ?? "directory"}`),
      identify: (id: string) => navigate(`/identify/${id}`),
      review: (id: string) => navigate(`/review/${id}`),
      export: (id: string) => navigate(`/export/${id}`),
      viewer: (id: string, file?: ViewerSrtFile) =>
        navigate(`/viewer/${id}/${file ?? "tagged.srt"}`),
      corrections: (id: string) => navigate(`/corrections/${id}`),
      structure: (id: string, tab?: StructureTab) =>
        navigate(`/structure/${id}/${tab ?? "sections"}`),
      report: (id: string) => navigate(`/report/${id}`),
      reviewReport: (id: string) => navigate(`/review-report/${id}`),
    }),
    [navigate],
  );
}

// ---------------------------------------------------------------------------
// useSyncParams — sincroniza URL params → átomos al montar y en cada cambio.
// ---------------------------------------------------------------------------

export function useSyncParams() {
  const params = useParams<{
    processingId?: string;
    srtFile?: string;
    tab?: string;
  }>();
  const setProcessingId = useSetAtom(currentProcessingIdAtom);

  // processingId
  useEffect(() => {
    setProcessingId(params.processingId ?? null);
  }, [params.processingId, setProcessingId]);

  // viewer SRT file
  const setViewerFile = useSetAtom(viewerSrtFileAtom);
  useEffect(() => {
    if (params.srtFile === "srt-confirmed.srt" || params.srtFile === "tagged.srt") {
      setViewerFile(params.srtFile);
    }
  }, [params.srtFile, setViewerFile]);

  // admin tab
  const setAdminTab = useSetAtom(adminTabAtom);
  useEffect(() => {
    if (params.tab === "directory" || params.tab === "glossary") {
      setAdminTab(params.tab);
    }
  }, [params.tab, setAdminTab]);

  // structure tab
  const setStructureTab = useSetAtom(structureTabAtom);
  useEffect(() => {
    if (
      params.tab === "sections" ||
      params.tab === "decisions" ||
      params.tab === "tasks"
    ) {
      setStructureTab(params.tab);
    }
  }, [params.tab, setStructureTab]);
}

// ---------------------------------------------------------------------------
// useRequireMeetingData — redirige a new-meeting si no hay datos cargados.
//
// Para pantallas que no fetchean sus propios datos (identify, review, export).
// Si el usuario hace F5 en estas pantallas, oracleJsonAtom es null y hay que
// pasar por el flujo de continuación de NewMeetingScreen para rehidratar.
// ---------------------------------------------------------------------------

export function useRequireMeetingData() {
  const oracleJson = useAtomValue(oracleJsonAtom);
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (processingId && oracleJson === null) {
      navigate(`/new-meeting/${processingId}`, { replace: true });
    }
  }, [processingId, oracleJson, navigate]);
}
